import {graphql} from 'react-relay';
import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {useForecastFetchQuery} from '../../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {useDebounce} from '../../../../forecast-app/ticketing/features/tickets/hooks/useDebounce';
import Util from '../../../../forecast-app/shared/util/util';
import {UseCandidatesOptions, ValueCandidate, ValueSource} from './valueSource.types';

function filterBySearch(items: ValueCandidate[], search: string): ValueCandidate[] {
	const q = search.trim().toLowerCase();
	return q ? items.filter(i => i.label.toLowerCase().includes(q)) : items;
}

/** Candidate order comes from whatever the backing endpoint happened to return, and not every one
 * of them orders its rows: `LabelLogic.getLabelsByType` (what `labels(labelType: TASK)` reaches)
 * has no ORDER BY at all, unlike the unfiltered `getLabelsByCompanyId` it replaced, which sorted by
 * name. Sort here so every picker in this family reads alphabetically regardless. */
function byLabel(items: ValueCandidate[]): ValueCandidate[] {
	// `numeric` so project codes read P1, P2, P10, P100 rather than P1, P10, P100, P2.
	return [...items].sort((a, b) => a.label.localeCompare(b.label, undefined, {numeric: true}));
}

/**
 * Builds a ValueSource backed by a single Relay query that returns a flat list of
 * {id, label} candidates. The query is fetched once on mount. resolveLabels reads
 * from a module-level cache populated by the last successful fetch, so a candidate's
 * label is available synchronously even for a component that never called useCandidates
 * (e.g. a summary line rendered elsewhere in the tree).
 */
export function createRelayListValueSource<T>(
	query: Parameters<typeof useForecastFetchQuery>[0],
	selectRows: (data: T | null) => ValueCandidate[]
): ValueSource {
	let labelCache: Record<string, string> = {};

	return {
		useCandidates(search: string) {
			const {data, fetch, loading} = useForecastFetchQuery<T, Record<string, unknown>>(query);
			const [error, setError] = useState(false);
			const runFetch = useCallback(() => {
				setError(false);
				fetch(undefined, undefined, () => setError(true));
				// empty deps intentionally keeps this stable across renders; fetch's identity is
				// re-created every render by useForecastFetchQuery, but it always closes over the
				// same query/environment, so calling the version captured here is equivalent
			}, []);
			useEffect(() => {
				runFetch();
				// empty deps intentionally runs the fetch once on mount; runFetch/selectRows are
				// omitted by design
			}, []);
			// Memoized on the payload: `useCandidates` re-renders on every keystroke in the picker's
			// search field, and re-selecting plus re-sorting a 10k-row list on each one is wasted work
			// - the rows only change when a fetch resolves.
			const items = useMemo(() => byLabel(selectRows(data)), [data]);
			useEffect(() => {
				if (items.length > 0) {
					labelCache = {...labelCache, ...Object.fromEntries(items.map(i => [i.id, i.label]))};
				}
			}, [items]);
			return {items: filterBySearch(items, search), loading, error, retry: runFetch};
		},
		resolveLabels(ids: string[]) {
			const out: Record<string, string> = {};
			ids.forEach(id => {
				if (labelCache[id] !== undefined) {
					out[id] = labelCache[id];
				}
			});
			return out;
		},
	};
}

// Verified against schema.graphql: Viewer.projects -> ProjectTypeConnection, ProjectType.{id, name}.
// Company.allProjects is NOT usable here: its resolver returns null unless a searchQuery with
// filters is supplied, so the builder's plain `first:` query came back empty with no error and
// the dropdown read "No matches" on an account with projects.
//
// excludeRestricted, like every other project filter dropdown in the app (FilterQueryContent,
// ProjectListPage, TimesheetsPage, MyNotifications): ProjectLogic.getAllProjects defaults it to
// false, and `if (onlyRestricted || !excludeRestricted)` then UNIONS in the projects the viewer has
// no access to. Offering one is a pick that can never match a row the report is allowed to show,
// and it puts a restricted project's name in front of someone who cannot open it.
const projectListQuery = graphql`
	query relayValueSourcesProjectQuery {
		viewer {
			projects(first: 10000, excludeRestricted: true) {
				edges {
					node {
						id
						name
						companyProjectId
						customProjectId
					}
				}
			}
		}
	}
`;

interface ProjectListQueryData {
	viewer?: {
		projects?: {
			edges?: Array<{
				node: {id: string; name: string | null; companyProjectId?: number | null; customProjectId?: string | null};
			}>;
		};
	};
}

/** A Relay global id is base64 of "Type:id"; the filter wire value is the raw integer id, which is
 * what the query engine compares and what the authoring agent already writes. Decoding here is what
 * lets a stored value resolve to its name instead of collapsing to "N selected".
 *
 * Returns nothing for a node that cannot produce a usable candidate: no name to show, or an id that
 * is not a relay global id. `Util.getIdFromBase64String` is `parseInt(atob(str).split(':')[1])`, so
 * it yields NaN for a decoded string with no numeric second half and throws for a string that is not
 * base64 at all. Either way the only candidate it could build is one whose id no row can match and
 * whose value the query engine cannot parse, so the option is dropped rather than offered. */
function toCandidate(id: string, name: string | null): ValueCandidate | null {
	if (!name) {
		return null;
	}
	let decoded: number;
	try {
		decoded = Util.getIdFromBase64String(id);
	} catch {
		return null;
	}
	return Number.isFinite(decoded) ? {id: String(decoded), label: name} : null;
}

/** The five selectors below differ only in where the edges live and which field carries the name,
 * so the map/filter/never-null plumbing is written once here. */
function toCandidates<N extends {id: string}>(
	edges: Array<{node: N}> | undefined,
	nameOf: (node: N) => string | null
): ValueCandidate[] {
	return (edges ?? []).map(e => toCandidate(e.node.id, nameOf(e.node))).filter((c): c is ValueCandidate => c !== null);
}

/** Counts labels case-insensitively, the way the picker's search compares them. */
function collisionCounts(labels: string[]): Map<string, number> {
	const counts = new Map<string, number>();
	labels.forEach(label => {
		const key = label.toLowerCase();
		counts.set(key, (counts.get(key) ?? 0) + 1);
	});
	return counts;
}

/** Appends ` #<id>` to every label another candidate shares (case-insensitively). The id is the
 * decoded internal id the condition stores, so the result is unique for distinct values and stable
 * across fetches. */
function withIdOnCollision(candidates: ValueCandidate[]): ValueCandidate[] {
	const counts = collisionCounts(candidates.map(c => c.label));
	return candidates.map(c => ((counts.get(c.label.toLowerCase()) ?? 0) > 1 ? {...c, label: `${c.label} #${c.id}`} : c));
}

/** The code the table's project pill shows (`shapeProject` in reportRowMapper): the custom project id
 * when there is one, else `P<companyProjectId>`; nothing for a zero or absent id. */
function projectCode(node: {companyProjectId?: number | null; customProjectId?: string | null}): string | null {
	if (node.customProjectId) {
		return node.customProjectId;
	}
	return node.companyProjectId != null && node.companyProjectId > 0 ? `P${node.companyProjectId}` : null;
}

export function selectProjectRows(data: ProjectListQueryData | null): ValueCandidate[] {
	// The code first, as the table's project pill shows it, so "p10" finds P10 the way "Test" finds its
	// name. The stored value is still the project id, so saved conditions are unaffected.
	return toCandidates(data?.viewer?.projects?.edges, node => {
		if (!node.name) {
			return null;
		}
		const code = projectCode(node);
		return code ? `${code} · ${node.name}` : node.name;
	});
}

export const projectValueSource: ValueSource = createRelayListValueSource<ProjectListQueryData>(
	projectListQuery,
	selectProjectRows
);

// Verified against schema.graphql: Company.allPersons -> PersonTypeConnection, Person.{id, fullName}.
const personListQuery = graphql`
	query relayValueSourcesPersonQuery {
		viewer {
			company {
				allPersons(first: 10000) {
					edges {
						node {
							id
							fullName
						}
					}
				}
			}
		}
	}
`;

interface PersonListQueryData {
	viewer?: {
		company?: {
			allPersons?: {
				edges?: Array<{node: {id: string; fullName: string | null}}>;
			};
		};
	};
}

export function selectPersonRows(data: PersonListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.allPersons?.edges, node => node.fullName);
}

export const personValueSource: ValueSource = createRelayListValueSource<PersonListQueryData>(
	personListQuery,
	selectPersonRows
);

// Verified against schema.graphql: Company.clients -> ClientTypeConnection, Client.{id, name}.
const clientListQuery = graphql`
	query relayValueSourcesClientQuery {
		viewer {
			company {
				clients(first: 10000) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface ClientListQueryData {
	viewer?: {
		company?: {
			clients?: {
				edges?: Array<{node: {id: string; name: string}}>;
			};
		};
	};
}

/** Client names are not unique on an account, so a name two clients share gets each one's internal
 * id appended, "Bit #12" and "Bit #40". The client type carries no other number a reader would know
 * it by. */
export function selectClientRows(data: ClientListQueryData | null): ValueCandidate[] {
	return withIdOnCollision(toCandidates(data?.viewer?.company?.clients?.edges, node => node.name));
}

export const clientValueSource: ValueSource = createRelayListValueSource<ClientListQueryData>(
	clientListQuery,
	selectClientRows
);

// labelType: TASK because a task report can only ever match a task label. Without it the dropdown
// offered every label on the account, including project-only and people-only ones, which are dead
// options that can never match a row.
// Verified against schema.graphql: Company.labels -> LabelTypeConnection, Label.{id, name}, and
// LABEL_TYPE enum includes TASK.
const labelListQuery = graphql`
	query relayValueSourcesLabelQuery {
		viewer {
			company {
				labels(first: 10000, labelType: TASK) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface LabelListQueryData {
	viewer?: {
		company?: {
			labels?: {
				edges?: Array<{node: {id: string; name: string | null}}>;
			};
		};
	};
}

export function selectLabelRows(data: LabelListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.labels?.edges, node => node.name);
}

export const labelValueSource: ValueSource = createRelayListValueSource<LabelListQueryData>(labelListQuery, selectLabelRows);

// labelType: PROJECT because a PROJECT STATUS report can only ever match a project label. The task
// query above pins TASK for the mirror-image reason. Two queries rather than one parameterised
// query: relay-compiler generates a separate artifact per query document, and the existing task
// query's comment records why the type is pinned at the document rather than passed at call time.
// Verified against schema.graphql: LABEL_TYPE includes PROJECT (5179).
const projectLabelListQuery = graphql`
	query relayValueSourcesProjectLabelQuery {
		viewer {
			company {
				labels(first: 10000, labelType: PROJECT) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

export function selectProjectLabelRows(data: LabelListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.labels?.edges, node => node.name);
}

export const projectLabelValueSource: ValueSource = createRelayListValueSource<LabelListQueryData>(
	projectLabelListQuery,
	selectProjectLabelRows
);

// Verified against schema.graphql: Company.roles -> RoleTypeConnection, Role.{id, name}.
// includeDisabled is false so a retired role is not offered as a filter value.
const roleListQuery = graphql`
	query relayValueSourcesRoleQuery {
		viewer {
			company {
				roles(first: 10000, includeDisabled: false) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface RoleListQueryData {
	viewer?: {
		company?: {
			roles?: {
				edges?: Array<{node: {id: string; name: string | null}}>;
			};
		};
	};
}

export function selectRoleRows(data: RoleListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.roles?.edges, node => node.name);
}

export const roleValueSource: ValueSource = createRelayListValueSource<RoleListQueryData>(roleListQuery, selectRoleRows);

// Workflow status candidates.
//
// schema.graphql exposes per-project custom status columns only
// (ProjectType.statusColumnsV2 / ProjectGroupType.statusColumns) — there is no
// company-wide connection enumerating status column names across all projects, and
// the ValueSource interface has no project-scoping parameter to fetch one project's
// columns. The only status classification that is global and requires no project
// context is the fixed STATUS_CATEGORY enum (TODO / INPROGRESS / DONE), which is the
// same category set the legacy "Task Status" filter dropdown lists via
// getWorkFlowCategoryFilters — that legacy dropdown never issues a Relay query either;
// it is a static local list. The candidate ids (TODO/INPROGRESS/DONE) are built with
// translated labels in `useValueSource`, which is why there is no exported
// `workflowStatusValueSource` constant here anymore.

// Verified against schema.graphql: Company.rateCards -> RateCardTypeConnection, RateCard.{id, name,
// disabled}. Disabled rate cards are fetched and then dropped below rather than filtered in the
// query, because the connection takes no filter argument.
const rateCardListQuery = graphql`
	query relayValueSourcesRateCardQuery {
		viewer {
			company {
				rateCards(first: 10000) {
					edges {
						node {
							id
							name
							disabled
						}
					}
				}
			}
		}
	}
`;

interface RateCardListQueryData {
	viewer?: {
		company?: {
			rateCards?: {
				edges?: Array<{node: {id: string; name: string | null; disabled: boolean | null}}>;
			};
		};
	};
}

/** A disabled rate card is one nobody may put a project on any more. Existing projects keep theirs,
 * so a report CAN legitimately contain rows carrying one - but offering it in the picker invites a
 * filter on a value that will only ever shrink, and the rest of this family already drops options
 * that cannot match (see the labelType note above). */
export function selectRateCardRows(data: RateCardListQueryData | null): ValueCandidate[] {
	const enabled = (data?.viewer?.company?.rateCards?.edges ?? []).filter(e => e.node.disabled !== true);
	return toCandidates(enabled, node => node.name);
}

export const rateCardValueSource: ValueSource = createRelayListValueSource<RateCardListQueryData>(
	rateCardListQuery,
	selectRateCardRows
);

// Verified against schema.graphql: Viewer.clientContacts -> ClientContactConnection,
// ClientContact.{id, firstName, lastName}. Viewer's connection is the account-wide pool; the
// per-client one (Client.clientContacts) would need a client already chosen, and this filter is
// authored without one.
const clientContactListQuery = graphql`
	query relayValueSourcesClientContactQuery {
		viewer {
			clientContacts(first: 10000) {
				edges {
					node {
						id
						firstName
						lastName
					}
				}
			}
		}
	}
`;

interface ClientContactListQueryData {
	viewer?: {
		clientContacts?: {
			edges?: Array<{node: {id: string; firstName: string | null; lastName: string | null}}>;
		};
	};
}

/** Joined around whichever part is present, matching how report-service builds the name it puts in
 * the Contact column: a blank-padded "Nora " reads as a data fault, and a lone space reads as a
 * contact with no name rather than as no contact. A contact with neither part is dropped by
 * toCandidates, since there is nothing to show for it. */
export function selectClientContactRows(data: ClientContactListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.clientContacts?.edges, node => {
		const joined = [node.firstName, node.lastName]
			.map(part => (part ?? '').trim())
			.filter(part => part.length > 0)
			.join(' ');
		return joined.length > 0 ? joined : null;
	});
}

export const clientContactValueSource: ValueSource = createRelayListValueSource<ClientContactListQueryData>(
	clientContactListQuery,
	selectClientContactRows
);

/** One page of a server-side search: the rows it returned, and how many match in all (null when the
 * server does not say). */
export interface SearchPage<R> {
	rows: R[];
	totalCount: number | null;
	/** How many rows the server returned, counting any the selector dropped. */
	returned: number;
	hasNextPage: boolean;
	/** Where the next page starts, or null when the server gave no cursor. */
	endCursor: string | null;
}

/** The cursor of the one further page worth fetching. When the matches a page left out are no more
 * than the selected values it does not hold, they may all be those selected values, which are listed
 * anyway; only the next page can show whether any unselected match is really missing. */
function continuationCursor<R extends {id: string}>(page: SearchPage<R>, selectedIds: string[]): string | null {
	if (!page.hasNextPage || page.endCursor === null || page.totalCount === null) {
		return null;
	}
	const onPage = new Set(page.rows.map(row => row.id));
	const selectedOffPage = selectedIds.filter(id => !onPage.has(id)).length;
	return page.totalCount - page.returned <= selectedOffPage ? page.endCursor : null;
}

function appendPage<R extends {id: string}>(first: SearchPage<R>, next: SearchPage<R>): SearchPage<R> {
	const seen = new Set(first.rows.map(row => row.id));
	return {
		rows: [...first.rows, ...next.rows.filter(row => !seen.has(row.id))],
		totalCount: first.totalCount,
		returned: first.returned + next.returned,
		hasNextPage: next.hasNextPage,
		endCursor: next.endCursor,
	};
}

/** Fetches the row of one stored value, for a value no page of the search holds. */
export interface RowLookup<L, R> {
	query: Parameters<typeof useForecastFetchQuery>[0];
	/** The query variables for one stored id, or null for an id that cannot be looked up. */
	variablesFor: (id: string) => Record<string, unknown> | null;
	/** The looked-up row, keyed by the same decoded id the condition stores. */
	selectRow: (data: L | null) => R | null;
}

export interface RelaySearchSourceConfig<T, R extends {id: string}, L> {
	query: Parameters<typeof useForecastFetchQuery>[0];
	/** The query variables for one search, already trimmed; '' asks for every row. `after` is the
	 * cursor of the page to continue from, and absent for the first page. */
	variablesFor: (search: string, after?: string) => Record<string, unknown>;
	selectPage: (data: T | null) => SearchPage<R>;
	/** Labels the rows together, so a label can depend on which other rows are present (a tie-break
	 * between two rows that would otherwise read the same). */
	labelRows: (rows: R[]) => ValueCandidate[];
	lookup?: RowLookup<L, R>;
}

/** How long the search text has to stop changing before it is sent. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Past this many, the labels remembered for rows that are not selected are dropped and rebuilt from
 * the next page, so a long session of searching does not keep every page it ever saw. */
export const MAX_REMEMBERED_PAGE_LABELS = 5000;

/**
 * Builds a ValueSource for a list too large to fetch whole. The server does the searching: the
 * search text is debounced and sent as a query variable, and the answer is one bounded page of
 * matches plus the total count, which `useCandidates` reports as `total` so the picker can say how
 * many it is not showing, and `hasNextPage` as `hasMore`. When the matches a page left out could all
 * be selected values, the next page is fetched before the answer is shown, so `hasMore` is only true
 * when an unselected match may really be missing. Only the answer to the latest request is kept, so
 * a slow response to an older search never replaces a newer one. Until the answer for the text now
 * in the field arrives, the previous page is shown filtered in memory and `pending` is true.
 *
 * A page row is labelled by `labelRows` over the page plus the rows of the selected values, so a page
 * row that would read the same as a selected value is widened away from it. A selected value no page
 * holds is fetched on its own through `lookup`, one id at a time once the first page has arrived. An
 * answer, including one that finds nothing, is kept for the session. A lookup that fails is not tried
 * again by the same component, so it never retries on every render, but the next component to mount
 * tries again. A lookup whose component unmounts before the answer lands is not counted, so the next
 * one retries.
 *
 * With `labelsOnly` no page is fetched at all, and every selected value not already known is looked
 * up straight away. That is what a summary needs to name stored values without paying for a page.
 *
 * `resolveLabels` reads module-level caches, so a value's label is available synchronously to a
 * component that never called `useCandidates`. Labels of selected values are always kept; labels of
 * other rows are kept up to `MAX_REMEMBERED_PAGE_LABELS`.
 */
export function createRelaySearchValueSource<T, R extends {id: string}, L = unknown>(
	config: RelaySearchSourceConfig<T, R, L>
): ValueSource {
	const {lookup} = config;
	let pageLabels: Record<string, string> = {};
	let pageLabelCount = 0;
	const selectedLabels = new Map<string, string>();
	/** Rows of selected values, kept so they can be labelled next to any later page. */
	const selectedRows = new Map<string, R>();
	const lookupAttempted = new Set<string>();
	const lookupInFlight = new Set<string>();

	const rememberPageLabels = (candidates: ValueCandidate[]): void => {
		if (pageLabelCount + candidates.length > MAX_REMEMBERED_PAGE_LABELS) {
			pageLabels = {};
			pageLabelCount = 0;
		}
		candidates.forEach(c => {
			if (pageLabels[c.id] === undefined) {
				pageLabelCount += 1;
			}
			pageLabels[c.id] = c.label;
		});
	};

	return {
		useCandidates(search: string, selectedIds: string[] = [], options: UseCandidatesOptions = {}) {
			const labelsOnly = options.labelsOnly === true;
			const term = search.trim();
			const debouncedTerm = useDebounce(term, SEARCH_DEBOUNCE_MS);
			const pageFetch = useForecastFetchQuery<T, Record<string, unknown>>(config.query);
			// Called unconditionally so the hook order never depends on whether this source has a
			// lookup; without one it is never fetched.
			const labelFetch = useForecastFetchQuery<L, Record<string, unknown>>(lookup ? lookup.query : config.query);
			const [page, setPage] = useState<{term: string; result: SearchPage<R>} | null>(null);
			const [error, setError] = useState(false);
			// Bumped when a lookup settles, so the labels are rebuilt with its row.
			const [lookedUp, markLookedUp] = useReducer((count: number) => count + 1, 0);
			const latestRequest = useRef(0);
			const mounted = useRef(true);
			// Read when a page lands, to decide whether the matches it left out could all be selected.
			const selectedRef = useRef(selectedIds);
			selectedRef.current = selectedIds;
			// Lookups that failed in this component. Kept per component so a failure is not retried on
			// every render here, while a later mount still gets to try again.
			const failedHere = useRef(new Set<string>());
			useEffect(
				() => () => {
					mounted.current = false;
				},
				[]
			);

			const runFetch = useCallback((searchTerm: string) => {
				latestRequest.current += 1;
				const request = latestRequest.current;
				const isLatest = (): boolean => mounted.current && request === latestRequest.current;
				setError(false);
				pageFetch.fetch(
					config.variablesFor(searchTerm),
					data => {
						if (!isLatest()) {
							return;
						}
						const first = config.selectPage(data);
						const after = continuationCursor(first, selectedRef.current);
						if (after === null) {
							setPage({term: searchTerm, result: first});
							return;
						}
						// Without the next page the first one is still a usable answer, just not a
						// complete one, so a failure here falls back to it rather than to an error.
						pageFetch.fetch(
							config.variablesFor(searchTerm, after),
							more => {
								if (isLatest()) {
									setPage({term: searchTerm, result: appendPage(first, config.selectPage(more))});
								}
							},
							() => {
								if (isLatest()) {
									setPage({term: searchTerm, result: first});
								}
							}
						);
					},
					() => {
						if (isLatest()) {
							setError(true);
						}
					}
				);
				// empty deps intentionally keeps this stable across renders; fetch's identity is
				// re-created every render by useForecastFetchQuery, but it always closes over the
				// same query/environment, so calling the version captured here is equivalent
			}, []);
			useEffect(() => {
				if (!labelsOnly) {
					runFetch(debouncedTerm);
				}
				// runFetch is stable; the fetch runs once per settled search text
			}, [debouncedTerm, labelsOnly]);

			const selectedKey = selectedIds.join(',');
			const {candidates, onPage} = useMemo(() => {
				const rows = page?.result.rows ?? [];
				const pageIds = new Set(rows.map(row => row.id));
				const selected = new Set(selectedIds);
				rows.forEach(row => {
					if (selected.has(row.id)) {
						selectedRows.set(row.id, row);
					}
				});
				const selectedKnown = selectedIds.map(id => selectedRows.get(id)).filter((row): row is R => row !== undefined);
				// A selected value is labelled against the other selected values only, never the page.
				// Every component given the same selection then writes the same label, whichever
				// rendered last, so the tile summary and the picker agree.
				config.labelRows(selectedKnown).forEach(c => selectedLabels.set(c.id, c.label));
				const offPage = selectedKnown.filter(row => !pageIds.has(row.id));
				const labelled = config.labelRows([...rows, ...offPage]);
				const pageCandidates = labelled.filter(c => pageIds.has(c.id));
				rememberPageLabels(pageCandidates);
				return {candidates: byLabel(pageCandidates), onPage: pageIds};
				// selectedKey stands in for selectedIds, whose identity changes every render
			}, [page, selectedKey, lookedUp]);

			// Waits for the first page, so only the ids it really lacks are looked up (unless no page is
			// coming), and for the previous lookup, so they go one at a time through the one fetch hook.
			const nextLookupId =
				lookup && (labelsOnly || page !== null) && !labelFetch.loading
					? selectedIds.find(
							id =>
								!onPage.has(id) &&
								!selectedRows.has(id) &&
								!lookupAttempted.has(id) &&
								!lookupInFlight.has(id) &&
								!failedHere.current.has(id) &&
								lookup.variablesFor(id) !== null
					  )
					: undefined;
			useEffect(() => {
				if (lookup && nextLookupId !== undefined) {
					const id = nextLookupId;
					lookupInFlight.add(id);
					labelFetch.fetch(
						lookup.variablesFor(id) ?? undefined,
						data => {
							lookupInFlight.delete(id);
							const row = lookup.selectRow(data);
							if (row) {
								selectedRows.set(row.id, row);
							}
							if (mounted.current) {
								lookupAttempted.add(id);
								markLookedUp();
							}
						},
						() => {
							lookupInFlight.delete(id);
							if (mounted.current) {
								failedHere.current.add(id);
								markLookedUp();
							}
						}
					);
				}
				// keyed on the id alone: the fetch function is re-created every render
			}, [nextLookupId]);

			const current = page !== null && page.term === term;
			return {
				// The server already matched this page against the text in the field. An older page is
				// narrowed in memory meanwhile, so the list follows the typing before the answer lands.
				items: current ? candidates : filterBySearch(candidates, term),
				loading: !labelsOnly && page === null && !error,
				pending: page !== null && !current,
				labelsLoading: labelFetch.loading || nextLookupId !== undefined,
				error,
				retry: () => runFetch(debouncedTerm),
				// Both withheld after a failed request, so neither is ever shown against a search it
				// does not describe.
				total: current && !error && page.result.totalCount !== null ? page.result.totalCount : undefined,
				hasMore: current && !error ? page.result.hasNextPage : undefined,
			};
		},
		resolveLabels(ids: string[]) {
			const out: Record<string, string> = {};
			ids.forEach(id => {
				const label = selectedLabels.get(id) ?? pageLabels[id];
				if (label !== undefined) {
					out[id] = label;
				}
			});
			return out;
		},
	};
}

/** The most sprints one search returns. The server clamps `first` to the same number. */
export const SPRINT_SEARCH_PAGE_SIZE = 200;

// `sprintSearch` is the company-wide sprint connection: it matches the search case-insensitively
// against the sprint name and leaves out sprints the viewer may not see (restricted projects, done or
// halted projects, no full access), so none of that is filtered here. `projectName` and
// `companyProjectId` are on the sprint itself for the label.
const sprintSearchQuery = graphql`
	query relayValueSourcesSprintSearchQuery($search: String, $first: Int, $after: String) {
		viewer {
			sprintSearch(search: $search, first: $first, after: $after) {
				totalCount
				pageInfo {
					hasNextPage
					endCursor
				}
				edges {
					node {
						id
						name
						projectName
						companyProjectId
						startYear
						startMonth
						startDay
						endYear
						endMonth
						endDay
					}
				}
			}
		}
	}
`;

interface SprintNode {
	id: string;
	name: string | null;
	projectName?: string | null;
	companyProjectId?: number | null;
	startYear?: number | null;
	startMonth?: number | null;
	startDay?: number | null;
	endYear?: number | null;
	endMonth?: number | null;
	endDay?: number | null;
}

interface SprintSearchQueryData {
	viewer?: {
		sprintSearch?: {
			totalCount?: number | null;
			pageInfo?: {hasNextPage?: boolean | null; endCursor?: string | null} | null;
			edges?: Array<{node: SprintNode | null} | null> | null;
		} | null;
	} | null;
}

/** A sprint as the label rule needs it, keyed by the decoded id the condition stores. */
export interface SprintRow {
	id: string;
	sprintName: string;
	projectName: string | null;
	projectNumber: number | null;
	dates: string | null;
}

function isoDate(year?: number | null, month?: number | null, day?: number | null): string | null {
	if (!year || !month || !day) {
		return null;
	}
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** "2024-01-08 – 2024-01-19", or whichever end is set, or null when the sprint has no dates. ISO
 * dates so the label does not depend on the reader's locale. */
export function sprintDateRange(sprint: SprintNode): string | null {
	const start = isoDate(sprint.startYear, sprint.startMonth, sprint.startDay);
	const end = isoDate(sprint.endYear, sprint.endMonth, sprint.endDay);
	if (start && end) {
		return `${start} – ${end}`;
	}
	return start ?? end;
}

/** Null for a sprint with no name or an undecodable id, which cannot be offered. */
function toSprintRow(sprint: SprintNode | null | undefined): SprintRow | null {
	if (!sprint || !sprint.name) {
		return null;
	}
	const candidate = toCandidate(sprint.id, sprint.name);
	if (!candidate) {
		return null;
	}
	return {
		id: candidate.id,
		sprintName: sprint.name,
		projectName: sprint.projectName ?? null,
		projectNumber: sprint.companyProjectId ?? null,
		dates: sprintDateRange(sprint),
	};
}

export function selectSprintSearchPage(data: SprintSearchQueryData | null): SearchPage<SprintRow> {
	const connection = data?.viewer?.sprintSearch;
	const edges = connection?.edges ?? [];
	const rows = edges.map(edge => toSprintRow(edge?.node)).filter((row): row is SprintRow => row !== null);
	return {
		rows,
		totalCount: connection?.totalCount ?? null,
		returned: edges.length,
		hasNextPage: connection?.pageInfo?.hasNextPage === true,
		endCursor: connection?.pageInfo?.endCursor ?? null,
	};
}

/** Sprint names are per project and routinely repeat ("Sprint 1"), so the label carries the project
 * name. Labels must be unique, so a label two sprints would still share is widened step by step, each
 * step applied only to the labels that still collide:
 *
 *   1. the project number, "Sprint 1 (Website, P-12)", for same-named projects. This matches the
 *      labels the authoring agent resolves against.
 *   2. the sprint's date range, for same-named sprints in one project.
 *   3. the sprint id, for anything still shared (no dates, or identical dates).
 *
 * Only the rows passed in are compared, so a label is unique among the sprints shown together. A
 * sprint whose project has no name starts from the bare sprint name. Collisions are compared
 * case-insensitively, as the search is. */
export function labelSprintRows(sprints: SprintRow[]): ValueCandidate[] {
	const rows = sprints.map(sprint => ({...sprint, parts: sprint.projectName ? [sprint.projectName] : []}));
	const labelOf = (row: (typeof rows)[number]): string =>
		row.parts.length > 0 ? `${row.sprintName} (${row.parts.join(', ')})` : row.sprintName;
	const widen = (extra: (row: (typeof rows)[number]) => string | null): void => {
		const counts = collisionCounts(rows.map(labelOf));
		rows.forEach(row => {
			if ((counts.get(labelOf(row).toLowerCase()) ?? 0) < 2) {
				return;
			}
			const part = extra(row);
			if (part) {
				row.parts = [...row.parts, part];
			}
		});
	};
	widen(row => (row.projectNumber !== null ? `P-${row.projectNumber}` : null));
	widen(row => row.dates);
	widen(row => `#${row.id}`);
	return rows.map(row => ({id: row.id, label: labelOf(row)}));
}

/** The labelled candidates of one search page on their own. */
export function selectSprintRows(data: SprintSearchQueryData | null): ValueCandidate[] {
	return labelSprintRows(selectSprintSearchPage(data).rows);
}

export function sprintSearchVariables(search: string, after?: string): {search: string | null; first: number; after?: string} {
	const variables = {search: search === '' ? null : search, first: SPRINT_SEARCH_PAGE_SIZE};
	return after === undefined ? variables : {...variables, after};
}

// Viewer.sprint(id) checks only that the viewer can open the sprint's project, not whether the project
// is still running, so it names a sprint stored on a condition before its project was closed, which
// the search leaves out.
const sprintLabelLookupQuery = graphql`
	query relayValueSourcesSprintLabelQuery($id: ID) {
		viewer {
			sprint(id: $id) {
				id
				name
				projectName
				companyProjectId
				startYear
				startMonth
				startDay
				endYear
				endMonth
				endDay
			}
		}
	}
`;

interface SprintLabelQueryData {
	viewer?: {sprint?: SprintNode | null} | null;
}

export function selectSprintLookupRow(data: SprintLabelQueryData | null): SprintRow | null {
	return toSprintRow(data?.viewer?.sprint);
}

/** A stored sprint value is the raw integer id; anything else cannot name a sprint. */
export function sprintLookupVariables(id: string): {id: string} | null {
	return /^[1-9][0-9]*$/.test(id) ? {id: btoa(`Sprint:${id}`)} : null;
}

export const sprintValueSource: ValueSource = createRelaySearchValueSource<
	SprintSearchQueryData,
	SprintRow,
	SprintLabelQueryData
>({
	query: sprintSearchQuery,
	variablesFor: sprintSearchVariables,
	selectPage: selectSprintSearchPage,
	labelRows: labelSprintRows,
	lookup: {
		query: sprintLabelLookupQuery,
		variablesFor: sprintLookupVariables,
		selectRow: selectSprintLookupRow,
	},
});
